import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { estimateService } from '../../services/estimateService';
import { Estimate, EstimateFilters, ESTIMATE_STATUS_LABELS } from '../../types/estimate';
import { formatCurrency } from '../../utils/formatters';

interface EstimateListProps {
  filters: EstimateFilters;
  onView?: (estimate: Estimate) => void;
  onEdit?: (estimate: Estimate) => void;
  onCreateNew?: () => void;
}

const statusStyle: Record<string, React.CSSProperties> = {
  DRAFT:    { background: 'rgba(120,120,140,0.15)', color: '#9a9aac', border: '1px solid rgba(120,120,140,0.25)' },
  SENT:     { background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' },
  ACCEPTED: { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  DECLINED: { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' },
  EXPIRED:  { background: 'rgba(201,140,90,0.12)', color: '#c98c5a', border: '1px solid rgba(201,140,90,0.25)' },
};

export const EstimateList: React.FC<EstimateListProps> = ({ filters, onView, onEdit, onCreateNew }) => {
  const [selectedEstimates, setSelectedEstimates] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: estimatesData, isLoading, error } = useQuery({
    queryKey: ['estimates', filters],
    queryFn: () => estimateService.getEstimates(filters)
  });

  const deleteMutation = useMutation({
    mutationFn: estimateService.deleteEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
    }
  });

  const markAsSentMutation = useMutation({
    mutationFn: estimateService.markEstimateAsSent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
    }
  });

  const acceptMutation = useMutation({
    mutationFn: estimateService.acceptEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
    }
  });

  const declineMutation = useMutation({
    mutationFn: estimateService.declineEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
    }
  });

  const handleSelectEstimate = (id: string) => {
    setSelectedEstimates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    const ids = estimatesData?.estimates.map(e => e.id) || [];
    setSelectedEstimates(selectedEstimates.length === ids.length ? [] : ids);
  };

  const handleDelete = (estimate: Estimate) => {
    if (window.confirm(`Delete estimate ${estimate.estimateNumber}?`)) deleteMutation.mutate(estimate.id);
  };
  const handleMarkAsSent = (estimate: Estimate) => {
    if (window.confirm(`Mark estimate ${estimate.estimateNumber} as sent?`)) markAsSentMutation.mutate(estimate.id);
  };
  const handleAccept = (estimate: Estimate) => {
    if (window.confirm(`Accept estimate ${estimate.estimateNumber}?`)) acceptMutation.mutate(estimate.id);
  };
  const handleDecline = (estimate: Estimate) => {
    if (window.confirm(`Decline estimate ${estimate.estimateNumber}?`)) declineMutation.mutate(estimate.id);
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString();
  const isExpiringSoon = (v: string) => { const d = estimateService.getDaysUntilExpiry(v); return d >= 0 && d <= 3; };
  const isExpired = (v: string) => estimateService.isEstimateExpired(v);

  const cardStyle: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid rgba(92,78,51,0.15)',
    borderRadius: 6,
  };

  const thStyle: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: '#d97706',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid rgba(92,78,51,0.15)',
    background: 'rgba(248,250,252,1)',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '13px 16px',
    fontFamily: "'Inter', sans-serif",
    fontSize: 12,
    color: '#5b5a52',
    borderBottom: '1px solid rgba(239,232,213,0.5)',
    whiteSpace: 'nowrap',
  };

  if (isLoading) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706' }}>Estimates</div>
        </div>
        <div style={{ padding: 20 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 14, alignItems: 'center' }}>
              <div style={{ flex: 1, height: 10, background: 'rgba(239,232,213,0.6)', borderRadius: 2 }} />
              <div style={{ width: 80, height: 10, background: 'rgba(241,245,249,1)', borderRadius: 2 }} />
              <div style={{ width: 60, height: 10, background: 'rgba(241,245,249,1)', borderRadius: 2 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={cardStyle}>
        <div style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>Failed to load estimates</p>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['estimates'] })}
            style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', marginTop: 8 }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const estimates = estimatesData?.estimates || [];

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706' }}>Estimates</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778', marginTop: 3 }}>
            {estimatesData?.pagination.totalItems ?? 0} total estimates
          </div>
        </div>
        <button
          onClick={onCreateNew}
          style={{
            fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #d97706, #b45309)',
            color: '#1c2033', border: 'none', padding: '9px 18px',
            borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          New Estimate
        </button>
      </div>

      {estimates.length === 0 ? (
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <svg width="40" height="40" fill="none" stroke="rgba(92,78,51,0.3)" viewBox="0 0 24 24" style={{ margin: '0 auto 16px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>No estimates</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginBottom: 24 }}>Get started by creating your first estimate.</div>
          <button
            onClick={onCreateNew}
            style={{
              fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #d97706, #b45309)',
              color: '#1c2033', border: 'none', padding: '11px 24px',
              borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            New Estimate
          </button>
        </div>
      ) : (
        <>
          {selectedEstimates.length > 0 && (
            <div style={{ padding: '10px 20px', background: 'rgba(241,245,249,1)', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                {selectedEstimates.length} selected
              </span>
              <button style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', background: 'none', border: 'none', cursor: 'pointer' }}>
                Bulk Delete
              </button>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 40 }}>
                    <input
                      type="checkbox"
                      checked={selectedEstimates.length === estimates.length && estimates.length > 0}
                      onChange={handleSelectAll}
                      style={{ accentColor: '#d97706', cursor: 'pointer' }}
                    />
                  </th>
                  <th style={thStyle}>Estimate #</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Job</th>
                  <th style={thStyle}>Issue Date</th>
                  <th style={thStyle}>Valid Until</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Total</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {estimates.map((estimate) => {
                  const expired = isExpired(estimate.validUntil);
                  const expiringSoon = isExpiringSoon(estimate.validUntil);
                  return (
                    <tr
                      key={estimate.id}
                      style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(248,250,252,1)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                    >
                      <td style={tdStyle}>
                        <input
                          type="checkbox"
                          checked={selectedEstimates.includes(estimate.id)}
                          onChange={() => handleSelectEstimate(estimate.id)}
                          style={{ accentColor: '#d97706', cursor: 'pointer' }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ color: '#fbbf24', fontWeight: 500 }}>{estimate.estimateNumber}</div>
                        <div style={{ fontSize: 11, color: '#8a8778', marginTop: 2 }}>
                          {estimate.items?.length || 0} item{(estimate.items?.length || 0) !== 1 ? 's' : ''}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div>{estimate.customer?.name}</div>
                        <div style={{ fontSize: 11, color: '#8a8778', marginTop: 2 }}>{estimate.customer?.email}</div>
                      </td>
                      <td style={tdStyle}>
                        {estimate.job ? (
                          <>
                            <div>{estimate.job.jobNumber}</div>
                            <div style={{ fontSize: 11, color: '#8a8778', marginTop: 2 }}>{estimate.job.title}</div>
                          </>
                        ) : (
                          <span style={{ color: '#8a8778' }}>—</span>
                        )}
                      </td>
                      <td style={tdStyle}>{formatDate(estimate.issueDate)}</td>
                      <td style={tdStyle}>
                        <span style={{ color: expired ? '#c95a5a' : expiringSoon ? '#c98c5a' : '#5b5a52' }}>
                          {formatDate(estimate.validUntil)}
                        </span>
                        {expired && <div style={{ fontSize: 10, color: '#c95a5a', marginTop: 2 }}>Expired</div>}
                        {expiringSoon && !expired && <div style={{ fontSize: 10, color: '#c98c5a', marginTop: 2 }}>Expiring soon</div>}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          ...statusStyle[estimate.status],
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 8,
                          fontWeight: 500,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          padding: '3px 8px',
                          borderRadius: 2,
                          display: 'inline-block',
                        }}>
                          {ESTIMATE_STATUS_LABELS[estimate.status]}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 500, color: '#fbbf24' }}>
                        {formatCurrency(estimate.total)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                          <button onClick={() => onView?.(estimate)}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer' }}>
                            View
                          </button>
                          {estimateService.canEditEstimate(estimate.status) && (
                            <button onClick={() => onEdit?.(estimate)}
                              style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Edit
                            </button>
                          )}
                          {estimate.status === 'DRAFT' && (
                            <button onClick={() => handleMarkAsSent(estimate)}
                              style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ac98c', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Send
                            </button>
                          )}
                          {estimate.status === 'SENT' && (
                            <>
                              <button onClick={() => handleAccept(estimate)}
                                style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ac98c', background: 'none', border: 'none', cursor: 'pointer' }}>
                                Accept
                              </button>
                              <button onClick={() => handleDecline(estimate)}
                                style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c98c5a', background: 'none', border: 'none', cursor: 'pointer' }}>
                                Decline
                              </button>
                            </>
                          )}
                          {estimateService.canDeleteEstimate(estimate.status) && (
                            <button onClick={() => handleDelete(estimate)}
                              style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {estimatesData?.pagination && estimatesData.pagination.totalPages > 1 && (
            <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778' }}>
                Showing {((estimatesData.pagination.currentPage - 1) * estimatesData.pagination.itemsPerPage) + 1}–
                {Math.min(estimatesData.pagination.currentPage * estimatesData.pagination.itemsPerPage, estimatesData.pagination.totalItems)} of {estimatesData.pagination.totalItems}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {[...Array(estimatesData.pagination.totalPages)].map((_, i) => {
                  const page = i + 1;
                  const active = page === estimatesData.pagination.currentPage;
                  return (
                    <button
                      key={i}
                      style={{
                        width: 28, height: 28,
                        fontFamily: "'Inter', sans-serif", fontSize: 9,
                        background: active ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(239,232,213,0.6)',
                        color: active ? '#1c2033' : '#8a8778',
                        border: active ? 'none' : '1px solid rgba(92,78,51,0.2)',
                        borderRadius: 3, cursor: 'pointer',
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
